import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { ChangeEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    PourTypeInput,
    PriceTierInput,
    PriceTierScopeInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import { BUSINESS_PRICE_TIER_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessPriceTier } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    businessPriceTier: BusinessPriceTier;
}

export const BusinessPriceTierDetailsCard = ({ businessPriceTier }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateBusinessPriceTier = useMutation({
        mutationKey: ['businessPriceTier', businessPriceTier.id],
        mutationFn: (data: BusinessPriceTier.UpdateRequest) =>
            Api.BusinessSpecific.PriceTier.putPriceTier(
                businessPriceTier.id,
                data
            ),
    });

    const formContext = useForm<BusinessPriceTier.UpdateRequest>({
        resolver: yupResolver(BUSINESS_PRICE_TIER_SCHEMA),
        defaultValues: businessPriceTier,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessPriceTier.UpdateRequest) => {
        mutateBusinessPriceTier
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated business price tier!', {
                    severity: 'success',
                });

                if (!data) {
                    return;
                }

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating business price tier!', {
                    severity: 'error',
                });
            });
    };

    const handleDefaultChange = (
        e: ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        if (checked) {
            formContext.setValue('defaultUpCharge', 0);
        }
    };

    const isDefault = useWatch({
        control: formContext.control,
        name: 'isDefault',
    });

    const tierScopeValue = useWatch({
        control: formContext.control,
        name: 'tierScope' as never,
    }) as 'type' | 'subtype' | undefined;

    const productSubtypeId = useWatch({
        control: formContext.control,
        name: 'productSubtypeId',
    });

    const tierScope = tierScopeValue ?? (productSubtypeId ? 'subtype' : 'type');

    return (
        <Card>
            <CardHeader title="BusinessPriceTier Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <PriceTierInput disabled={!editable} />
                        <PourTypeInput disabled={!editable} />
                        <PriceTierScopeInput disabled={!editable} />
                        {tierScope === 'type' ? (
                            <ProductTypeInput disabled={!editable} />
                        ) : (
                            <ProductSubtypeInput disabled={!editable} />
                        )}

                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            type="number"
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                            disabled={!editable}
                        />
                        {!isDefault && (
                            <TextFieldElement
                                label="Default Up Charge"
                                name="defaultUpCharge"
                                type="number"
                                fullWidth
                                slotProps={{
                                    htmlInput: {
                                        max: 1000,
                                        min: 0,
                                    },
                                }}
                                disabled={!editable}
                            />
                        )}
                        <CheckboxElement
                            label="Default"
                            name="isDefault"
                            disabled={!editable}
                            onChange={handleDefaultChange}
                        />

                        <Can I="update" a="BusinessPriceTier">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
